import React, { useMemo, useState, useEffect, useCallback } from "react";
import AppSDK from "@growthos/ui-app-sdk";
import { ToastContainer } from "@acadia-ui/toast";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import MasterCampaignsStore from "../../Flux/Stores/MasterCampaignsStore";
import { fetchAuditLevelConfig, fetchWorkbookActivationCampaigns, fetchEnabledColumns, fetchPlacementAPIConfig } from "../../Flux/Actions/WorkbookActions";
import { putFilterData } from "../../Flux/Actions/BookFilterActions";
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import WorkbookStore from "../../Flux/Stores/WorkbookStore";
import { putCampaignUniqueID, fetchCampaignsOnWorkbookPage, holdMasterCampaignUniqueID, fetchActivationHeaders } from "../../Flux/Actions/MasterCampaignActions";

import TaxonomyString from "../../Components/Tooltips/TaxonomyString/TaxonomyString";
import SyncTooltip from "../../Components/Tooltips/SyncTooltip/SyncTooltip";
import ErrorTooltip from "../../Components/Tooltips/ErrorTooltip/ErrorTooltip";
import GroupNumberTooltip from "../../Components/GroupNumberTooltip/GroupNumberTooltip";

import CampaignInformationRibbon from "./CampaignInformationRibbon";
import AuditLevelTabs from "./AuditLevelTabs";
import NavigationActions from "./NavigationActions";

import "./../views.scss";
import "./index.scss";
import { checkIfCampaignJIRATicket } from "../../Flux/Actions/PlacementActions";

const classNameComponent = `gos-workbook`;
const classNamePrefix = `${classNameComponent}__`;

const useDataStuffs = () => {
	const [filterChoices, setFilterChoices] = useState();

	const [auditLevels, setAuditLevels] = useState(null);
	const [metaData, setMetaData] = useState({});
	const [availableActivations, setAvailableActivations] = useState([]);
	const [templateExists, setTemplateExists] = useState(0);
	const [campaignJiraTicketLink, setCampaignJiraTicketLink] = useState(null);
	const [campaignJiraTicketExists, setCampaignJiraTicketExists] = useState(false);
	const [isHidden, setIsHidden] = useState(false);
	const [parentCampaignUniqueID, setParentCampaignUniqueID] = useState(null);
	const [masterCampaignUniqueID, setMasterCampaignUniqueID] = useState(null);
	const [placementAPI, setPlacementAPI] = useState(null);
	const [JIRAStatus, setJIRAStatus] = useState(null);
	const [defaultTab, setDefaultTab] = useState(0);

	const [params] = useSearchParams();
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const getAuditLevelConfig = useCallback((platformID) => {
		const filters = BookFiltersStore.getChoicesState();
		setFilterChoices(filters);
		const dataReq = fetchAuditLevelConfig(platformID);
		dataReq.then(() => {
			setAuditLevels(WorkbookStore.getAuditLevels());
			setIsHidden(false);
		});
	}, []);

	const fetchWorkbookMetaData = useCallback(() => {
		const uniqueCampaignID = MasterCampaignsStore.getParentCampaignUniqueID();
		if (!uniqueCampaignID) {
			navigate("/");
			return;
		}
		fetchWorkbookActivationCampaigns(uniqueCampaignID).then(() => {
			setAvailableActivations(WorkbookStore.getAvailableActivations());
			const meta = WorkbookStore.getMetaData();
			const { PlatformID, PlatformName, CampaignUniqueIDCode } = meta;
			setMetaData(meta);
			getPlatformStatuses(PlatformID);
			setTemplateExists(WorkbookStore.getTemplateExists());
			setCampaignJiraTicketLink(WorkbookStore.getCampaignJiraTicketLink());
			putFilterData("platform", { value: PlatformID, label: PlatformName });
			getAuditLevelConfig(PlatformID);
			fetchEnabledColumns(CampaignUniqueIDCode);
			checkIfCampaignJIRATicket(CampaignUniqueIDCode).then((response) => {
				setCampaignJiraTicketExists(response);
			});
			const campaignData = MasterCampaignsStore.getData();
			const activationConfig = MasterCampaignsStore.getActivationConfig();
			if (campaignData.length === 0) {
				fetchCampaignsOnWorkbookPage(WorkbookStore.getMetaData().CampaignUniqueIDCode).then(() => {
					if (!activationConfig?.length) {
						getActivationHeaders();
					}
				});
			} else {
				if (!activationConfig?.length) {
					getActivationHeaders();
				}
			}
		});
	}, [getAuditLevelConfig]);

	const hideWorkbook = useCallback(() => {
		setAuditLevels([]);
		setIsHidden(true);
	}, []);

	const getActivationHeaders = useCallback(() => {
		const matchingMasterCampaign = MasterCampaignsStore.getData().find((masterCampaign) => {
			return masterCampaign.MasterCampaignID.toString() === WorkbookStore.getMetaData()?.MasterCampaignID.toString();
		});
		if (matchingMasterCampaign) {
			let matchingActivationCampaign = null;
			Object.keys(matchingMasterCampaign.ActivationCampaigns).forEach((platform) => {
				matchingActivationCampaign ||= matchingMasterCampaign.ActivationCampaigns[platform].find((activationCampaign) => {
					return activationCampaign.CampaignID.toString() === WorkbookStore.getMetaData().CampaignUniqueIDCode.toString();
				});
			});

			if (matchingActivationCampaign) {
				const filterChoices = { platform: { value: WorkbookStore.getMetaData().PlatformID } };
				fetchActivationHeaders(filterChoices, matchingActivationCampaign?.HierarchyLevelID);
			}
		}
	}, []);

	const checkFilters = useCallback(() => {
		const contextValues = BookFiltersStore.getContextValues();
		if (contextValues.agency && contextValues.client && contextValues.region && contextValues.country) {
			const queryString = window.location.search;
			const urlParams = new URLSearchParams(queryString);
			const hierarchyLevelID = urlParams.get("hierarchyLevelID");
			const platformID = urlParams.get("platformID");
			const campaignID = urlParams.get("campaignID");
			const masterCampaignID = urlParams.get("masterCampaignID");
			const packageTX = urlParams.get("Package");
			const placementTX = urlParams.get("Placement");

			if (packageTX) {
				setDefaultTab(0);
			}

			if (placementTX) {
				setDefaultTab(1);
			}

			if (hierarchyLevelID && campaignID && platformID && masterCampaignID) {
				holdMasterCampaignUniqueID(masterCampaignID);
				putCampaignUniqueID(campaignID);
				fetchWorkbookMetaData();
				if (!packageTX && !placementTX) {
					// window.history.replaceState({}, document.title, "/Workbook");
				}
			} else {
				fetchWorkbookMetaData();
			}
		} else {
			hideWorkbook();
		}
	}, [fetchWorkbookMetaData, hideWorkbook]);

	const resetWorkbook = useCallback(() => {
		WorkbookStore.getAuditLevels().forEach((auditLevel) => {
			const auditLevelTab = document.getElementById(auditLevel.AuditLevelName);
			if (auditLevelTab) {
				// ReactDOM.unmountComponentAtNode(auditLevelTab);
			}
		});
		setParentCampaignUniqueID(MasterCampaignsStore.getParentCampaignUniqueID());
		setMasterCampaignUniqueID(MasterCampaignsStore.getMasterCampaignUniqueID());
		setAuditLevels([]);
		setIsHidden(true);
		fetchWorkbookMetaData();
	}, [fetchWorkbookMetaData]);

	const updateWorkbook = useCallback(
		(campaignID) => {
			const matchingMasterCampaign = MasterCampaignsStore.getData().find((masterCampaign) => {
				return masterCampaign.MasterCampaignID.toString() === WorkbookStore.getMetaData()?.MasterCampaignID.toString();
			});
			if (matchingMasterCampaign) {
				let matchingActivationCampaign = null;
				Object.keys(matchingMasterCampaign.ActivationCampaigns).forEach((platform) => {
					matchingActivationCampaign ||= matchingMasterCampaign.ActivationCampaigns[platform].find((activationCampaign) => {
						return activationCampaign.CampaignID.toString() === campaignID.toString();
					});
				});
				if (matchingActivationCampaign) {
					const queryParams = new URLSearchParams(window.location.search);
					queryParams.set("hierarchyLevelID", matchingActivationCampaign.HierarchyLevelID);
					queryParams.set("platformID", matchingActivationCampaign.MasterPlatformID);
					queryParams.set("campaignID", matchingActivationCampaign.CampaignID);
					queryParams.set("masterCampaignID", matchingMasterCampaign.MasterCampaignID);
					const args = `/Workbook/?${queryParams.toString()}`;
					navigate(args, { replace: true });
				}
			}
		},
		[checkFilters, resetWorkbook, navigate]
	);

	const fetchMetaData = useCallback(() => {
		const uniqueCampaignID = MasterCampaignsStore.getParentCampaignUniqueID();
		if (uniqueCampaignID) {
			fetchWorkbookActivationCampaigns(uniqueCampaignID).then(() => {
				setMetaData(WorkbookStore.getMetaData());
				getPlatformStatuses();
			});
		}
	}, []);

	const getPlatformStatuses = useCallback(
		(overridePlatformId) => {
			if (!metaData.PlatformID && !overridePlatformId) return;

			const contextValues = BookFiltersStore.getContextValues();
			if (contextValues.agency && contextValues.client && contextValues.region && contextValues.country) {
				const config = {
					platformID: overridePlatformId || metaData.PlatformID
				};
				fetchPlacementAPIConfig(config).then(() => {
					setPlacementAPI(WorkbookStore.getPlacementAPIConfigStatus());
					setJIRAStatus(WorkbookStore.getJIRAStatus());
				});
			}
		},
		[metaData]
	);

	useEffect(() => {
		if (!pathname?.startsWith("/Workbook")) {
			return;
		}
		document.title = "Placement Manager | Workbook";

		checkFilters();
		setParentCampaignUniqueID(MasterCampaignsStore.getParentCampaignUniqueID());
		setMasterCampaignUniqueID(MasterCampaignsStore.getMasterCampaignUniqueID());
		TaxonomyString.loadTaxonomyString("workbook__placements__taxonomy");
		TaxonomyString.loadTaxonomyString("workbook__campaign__taxonomy");
		SyncTooltip.loadTaxonomyString("workbook_placements_sync_taxonomy");
		SyncTooltip.loadTaxonomyString("workbook_packages_sync_taxonomy");
		ErrorTooltip.loadTaxonomyString("workbook_error_tooltip");
		GroupNumberTooltip.loadGroupNumber("group_number_placements");

		const handleClick = (event) => TaxonomyString.closeOpenTaxonomyString(event);
		window.addEventListener("click", handleClick, false);
		MasterCampaignsStore.on("campaignID_updated", resetWorkbook);

		return () => {
			window.removeEventListener("click", handleClick, false);
			MasterCampaignsStore.removeListener("campaignID_updated", resetWorkbook);
		};
	}, [params.toString()]);

	return useMemo(
		() => ({
			// this comment exists to prevent eslint from making this unreadable.
			auditLevels,
			metaData,
			availableActivations,
			templateExists,
			isHidden,
			parentCampaignUniqueID,
			masterCampaignUniqueID,
			updateWorkbook,
			fetchMetaData,
			filterChoices,
			placementAPI,
			JIRAStatus,
			defaultTab,
			campaignJiraTicketLink,
			campaignJiraTicketExists
		}),
		[
			placementAPI,
			JIRAStatus,
			auditLevels,
			metaData,
			availableActivations,
			templateExists,
			isHidden,
			parentCampaignUniqueID,
			masterCampaignUniqueID,
			updateWorkbook,
			fetchMetaData,
			filterChoices,
			defaultTab,
			campaignJiraTicketLink,
			campaignJiraTicketExists
			// params.toString()
		]
	);
};

const WorkBook = (props) => {
	const {
		// this comment exists to prevent eslint from making this unreadable.
		auditLevels,
		metaData,
		availableActivations,
		templateExists,
		parentCampaignUniqueID,
		masterCampaignUniqueID,
		updateWorkbook,
		fetchMetaData,
		filterChoices,
		placementAPI,
		JIRAStatus,
		defaultTab,
		campaignJiraTicketLink,
		campaignJiraTicketExists
	} = useDataStuffs(props);

	const [selectedAuditLevel, setSelectedAuditLevel] = useState(null);
	const [params] = useSearchParams();

	const actions = useMemo(() => {
		if (metaData && selectedAuditLevel) {
			return (
				<NavigationActions
					// this comment exists to prevent eslint from making this unreadable.
					metaData={metaData}
					selectedAuditLevel={selectedAuditLevel}
					templateExists={templateExists}
					fetchMetaData={fetchMetaData}
				/>
			);
		}
		return <></>;
	}, [metaData, selectedAuditLevel, templateExists, fetchMetaData]);

	const element = useMemo(() => {
		if (metaData && availableActivations && parentCampaignUniqueID && masterCampaignUniqueID && auditLevels) {
			return (
				<div className={classNameComponent}>
					<ToastContainer id="workbookToasts" />
					<div className={`${classNamePrefix}body`}>
						<CampaignInformationRibbon
							activationCampaignID={parentCampaignUniqueID}
							masterCampaignID={masterCampaignUniqueID}
							metaData={metaData}
							availableActivations={availableActivations}
							templateExists={templateExists}
							onUpdate={updateWorkbook}
							fetchMetaData={fetchMetaData}
						/>
						<AuditLevelTabs
							auditLevels={auditLevels}
							metaData={metaData}
							parentCampaignUniqueID={parentCampaignUniqueID}
							setSelectedAuditLevel={setSelectedAuditLevel}
							fetchMetaData={fetchMetaData}
							JIRAStatus={JIRAStatus}
							placementAPI={placementAPI}
							defaultTab={defaultTab}
							campaignJiraTicketExists={campaignJiraTicketExists}
						/>
					</div>
				</div>
			);
		}
		// return <></>;
		return <ToastContainer id="workbookToasts" />;
	}, [
		classNameComponent,
		classNamePrefix,
		metaData,
		availableActivations,
		templateExists,
		parentCampaignUniqueID,
		masterCampaignUniqueID,
		auditLevels,
		placementAPI,
		JIRAStatus,
		filterChoices,
		defaultTab,
		campaignJiraTicketExists,
		params.toString()
	]);

	return useMemo(
		() => ({
			element,
			label: metaData?.CampaignDescription || <div className="gos-skeleton gos-skeleton-1-1" style={{ width: 300, height: 43 }} />,
			actions: (
				<NavigationActions
					// this comment exists to prevent eslint from making this unreadable.
					metaData={metaData || {}}
					selectedAuditLevel={selectedAuditLevel}
					templateExists={templateExists}
					fetchMetaData={fetchMetaData}
					campaignJiraTicketLink={campaignJiraTicketLink}
				/>
			)
		}),
		[element, metaData, actions, selectedAuditLevel, templateExists, fetchMetaData, campaignJiraTicketLink]
	);
};

export default WorkBook;
